import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Box,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  InputLabelProps,
  Stack,
  TextFieldProps,
  Typography,
} from '@mui/material';
import {
  LoadingButton,
  LocalizationProvider,
  MobileDatePicker,
} from '@mui/lab';
import { DateRange as DateRangeIcon } from '@mui/icons-material';
import AdapterMoment from '@mui/lab/AdapterMoment';
import PhoneInput, {
  DefaultInputComponentProps,
} from 'react-phone-number-input';
import moment, { Moment } from 'moment';

import { useAppSelector } from '../../../../hooks';
import {
  availabilityOptions,
  Countries,
  countriesOptions,
  genderOptions,
  noticePeriodOptions,
  strengthOptions,
  teachingStatusOptions,
  TP_COLORS,
  yesOrNoBoolOptions,
} from '../../../../constants';
import { checkUserPermission } from '../../../../helper';

import { getAuthState } from '../../../../store/AuthSlice';
import { TUpdateUser, updateUser } from '../../../../store/UserSlice/UserApi';
import {
  getCurriculumsV2,
  getCurriculumV2State,
} from '../../../../store/V2/CurriculumSlice';

import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../components/Inputs';
import BankDetails from './BankDetails';
import { DashedDivider, primaryButtonProps } from '..';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../store/V2/RolesSlice';
import { getTenantsState } from '../../../../store/V2/TenantsSlice';

const deviceTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  ? Intl.DateTimeFormat().resolvedOptions().timeZone
  : 'Asia/Singapore';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let defaultCountryCode: any = 'SG';

const defaultUserTimezone = Countries.find(
  (country) => country.value === deviceTimeZone
);

if (defaultUserTimezone && defaultUserTimezone.countryCode) {
  defaultCountryCode = defaultUserTimezone.countryCode;
}

type TSelectOption = {
  label: string;
  value: string;
};

type TSelectOptionBool = {
  label: string;
  value: boolean;
};

type TSelectOptionNum = {
  label: string;
  value: number;
};

export type TSelectChange =
  | TSelectOption
  | TSelectOptionBool
  | TSelectOptionNum;

export const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const phoneInputProps: DefaultInputComponentProps = {
  international: true,
  className: 'phone-number-input-custom-styles',
  style: {
    height: '52px',
  },
  numberInputProps: {
    style: {
      height: '52px',
      borderColor: '#CBD5E1',
      fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: '1.4375em',
    },
  },
};

const phoneInputLabelProps: InputLabelProps = {
  shrink: true,
  sx: { color: TP_COLORS.black, backgroundColor: TP_COLORS.white },
};

export type TState = {
  firstName: string;
  lastName: string;
  gender?: TSelectOption | null;
  dateOfBirth?: Moment | null;
  email: string;
  billingEmail?: string;
  phoneNumber: string;
  country?: TSelectOption | null;
  selectedCurriculum?: TSelectOption | null;
  selectedGrade?: TSelectOption | null;
  examDate?: Moment | null;
  schoolName?: string;
  parentName?: string;
  parentEmail?: string;
  parentPhoneNumber?: string;
  nationality?: string;
  emergencyNumber?: string;
  teachingStatus?: TSelectOption | null;
  availability?: TSelectOption | null;
  isCameraOn?: TSelectOptionBool | null;
  trialNoticePeriod?: TSelectOptionNum | null;
  classNoticePeriod?: TSelectOptionNum | null;
  strengths: string[];
  bio?: string;
  bankCountry?: TSelectOption | null;
  bankName?: string;
  bankCode?: string;
  branchCode?: string;
  bankBranch?: string;
  accountNo?: string;
  accountHolderName?: string;
};

const initialState: TState = {
  firstName: '',
  lastName: '',
  gender: null,
  dateOfBirth: null,
  email: '',
  phoneNumber: '',
  country: null,
  selectedCurriculum: null,
  selectedGrade: null,
  examDate: null,
  schoolName: '',
  parentName: '',
  parentEmail: '',
  parentPhoneNumber: '',
  nationality: '',
  emergencyNumber: '',
  teachingStatus: null,
  availability: null,
  isCameraOn: null,
  trialNoticePeriod: null,
  classNoticePeriod: null,
  strengths: [],
  bio: '',
  bankCountry: null,
  bankName: '',
  bankCode: '',
  branchCode: '',
  bankBranch: '',
  accountNo: '',
  accountHolderName: '',
  billingEmail: '',
};

const BasicDetails: React.FC = () => {
  const dispatch = useDispatch();

  // State
  const [state, setState] = useState(initialState);
  const [isLoading, setLoading] = useState(false);
  const [gradeOptions, setGradeOptions] = useState<Array<TSelectOption>>([]);
  const [curriculumOptions, setCurriculumOptions] = useState<
    Array<TSelectOption>
  >([]);

  // Store
  const authState = useAppSelector(getAuthState);
  const tenantState = useAppSelector(getTenantsState);
  const curriculumState = useAppSelector(getCurriculumV2State);

  const { appTenant } = tenantState;
  const { user: authUser } = authState;

  const dateOfBirthPermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      profile: [USER_PERMISSION_INDIVIDUALS.PROFILE_DATE_OF_BIRTH],
    },
  });

  const nationalityPermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      profile: [USER_PERMISSION_INDIVIDUALS.PROFILE_NATIONALITY],
    },
  });

  const emergencyNumberPermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      profile: [USER_PERMISSION_INDIVIDUALS.PROFILE_EMERGENCY_NUMBER],
    },
  });

  const tutorDetailsPermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      profile: [USER_PERMISSION_INDIVIDUALS.PROFILE_TUTOR],
    },
  });

  const billingEmailPermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      profile: [USER_PERMISSION_INDIVIDUALS.PROFILE_BILLING_EMAIL],
    },
  });

  const isSubmitDisabled =
    !state.firstName.trim().length ||
    !state.lastName.trim().length ||
    !state.gender ||
    !state.email.trim().length ||
    !state.phoneNumber.trim().length ||
    !state.country ||
    authState.status === 'loading' ||
    (dateOfBirthPermission && !state.dateOfBirth) ||
    (nationalityPermission && !state.nationality?.trim().length) ||
    (emergencyNumberPermission && !state.emergencyNumber?.trim().length) ||
    (tutorDetailsPermission &&
      (!state.teachingStatus ||
        !state.availability ||
        !state.isCameraOn ||
        !state.strengths.length)) ||
    (tutorDetailsPermission &&
      (!state.teachingStatus ||
        !state.availability ||
        !state.isCameraOn ||
        !state.strengths.length));

  useEffect(() => {
    if (authUser) {
      setState((prevState) => {
        const newState: TState = {
          ...prevState,
          firstName: authUser.firstname,
          lastName: authUser.lastname || '',
          email: authUser.email,
          phoneNumber: authUser.phoneNumber || '',
          parentName: authUser.parentName || '',
          parentEmail: authUser.parentEmail || '',
          parentPhoneNumber: authUser.parentPhoneNumber || '',
          schoolName: authUser.schoolName || '',
          nationality: authUser.nationality || '',
          emergencyNumber: authUser.emergencyNumber || '',
          billingEmail: authUser.billingEmail || '',
        };

        if (authUser.gender) {
          const selectedValue = genderOptions.find(
            (item) => item.value === authUser.gender
          );
          newState.gender = selectedValue;
        }

        if (authUser.dateOfBirth) {
          newState.dateOfBirth = moment(authUser.dateOfBirth);
        }

        if (authUser.curriculum) {
          newState.selectedCurriculum = {
            label: authUser.curriculum,
            value: authUser.curriculum,
          };
        }

        if (authUser.grade) {
          newState.selectedGrade = {
            label: authUser.grade,
            value: authUser.grade,
          };
        }

        if (authUser.country) {
          const selectedValue = countriesOptions.find(
            (item) => item.value === authUser.country
          );
          newState.country = selectedValue;
        }

        if (authUser.examDate) {
          newState.examDate = moment(authUser.examDate);
        }

        // Tutor Details
        if (authUser.tutorDetails?.teachingStatus) {
          newState.teachingStatus = {
            label: authUser.tutorDetails.teachingStatus,
            value: authUser.tutorDetails.teachingStatus,
          };
        }

        if (authUser.tutorDetails?.availability) {
          const selectedValue = availabilityOptions.find(
            (item) => item.value === authUser.tutorDetails?.availability
          );
          newState.availability = selectedValue;
        }

        if (authUser.tutorDetails?.isCameraOn) {
          newState.isCameraOn = {
            label: authUser.tutorDetails.isCameraOn ? 'Yes' : 'No',
            value: authUser.tutorDetails.isCameraOn,
          };
        }

        if (authUser.tutorDetails?.trialNoticePeriod) {
          const selectedValue = noticePeriodOptions.find(
            (item) => item.value === authUser.tutorDetails?.trialNoticePeriod
          );
          newState.trialNoticePeriod = selectedValue;
        }

        if (authUser.tutorDetails?.classNoticePeriod) {
          const selectedValue = noticePeriodOptions.find(
            (item) => item.value === authUser.tutorDetails?.classNoticePeriod
          );
          newState.classNoticePeriod = selectedValue;
        }

        if (authUser.tutorDetails?.strengths) {
          newState.strengths = authUser.tutorDetails?.strengths;
        }

        if (authUser.tutorDetails?.bio) {
          newState.bio = authUser.tutorDetails.bio;
        }

        // Bank Details
        if (authUser.bankDetails?.bankCountry) {
          const selectedValue = countriesOptions.find(
            (item) => item.value === authUser.bankDetails?.bankCountry
          );
          if (selectedValue) {
            newState.bankCountry = selectedValue;
          }
        }
        if (authUser.bankDetails?.bankName) {
          newState.bankName = authUser.bankDetails.bankName;
        }
        if (authUser.bankDetails?.bankCode) {
          newState.bankCode = authUser.bankDetails.bankCode;
        }
        if (authUser.bankDetails?.branchCode) {
          newState.branchCode = authUser.bankDetails.branchCode;
        }
        if (authUser.bankDetails?.bankBranch) {
          newState.bankBranch = authUser.bankDetails.bankBranch;
        }
        if (authUser.bankDetails?.accountNo) {
          newState.accountNo = authUser.bankDetails.accountNo;
        }
        if (authUser.bankDetails?.accountHolderName) {
          newState.accountHolderName = authUser.bankDetails.accountHolderName;
        }

        return newState;
      });
    }
  }, [authUser]);

  useEffect(() => {
    dispatch(getCurriculumsV2());
  }, []);

  useEffect(() => {
    if (curriculumState.list && curriculumState.list.length) {
      setCurriculumOptions(
        curriculumState.list.map((curriculumItem) => ({
          label: curriculumItem.curriculum,
          value: curriculumItem.curriculum,
        }))
      );

      const matchingCurriculum = curriculumState.list.find(
        (curriculumItem) =>
          curriculumItem.curriculum === state.selectedCurriculum?.value
      );

      if (
        matchingCurriculum &&
        matchingCurriculum.subjects &&
        matchingCurriculum.subjects.length
      ) {
        const [firstSubject] = matchingCurriculum.subjects;

        if (firstSubject && firstSubject.grade && firstSubject.grade.length) {
          const newGradeOptions: TSelectOption[] = [];

          firstSubject.grade.forEach((gradeItem) => {
            newGradeOptions.push({
              label: gradeItem.grade,
              value: gradeItem.grade,
            });
          });

          setGradeOptions(newGradeOptions);
        }
      }
    }
  }, [curriculumState.list]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.currentTarget;

    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleCurriculumChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    const newGradeOptions: TSelectOption[] = [];

    if (curriculumState.list && curriculumState.list.length && value) {
      const selectedCurriculumData = curriculumState.list.find(
        (item) => item.curriculum === value.value
      );

      if (
        selectedCurriculumData &&
        selectedCurriculumData.subjects &&
        selectedCurriculumData.subjects.length
      ) {
        const [firstSubject] = selectedCurriculumData.subjects;

        if (firstSubject && firstSubject.grade && firstSubject.grade.length) {
          firstSubject.grade.forEach((gradeItem) => {
            newGradeOptions.push({
              label: gradeItem.grade,
              value: gradeItem.grade,
            });
          });
        }
      }
    }

    setState((prevState) => ({
      ...prevState,
      selectedCurriculum: value,
      selectedGrade: null,
    }));

    setGradeOptions(newGradeOptions);
  };

  const handleGradeChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedGrade: value,
    }));
  };

  const handleExamDateChange = (value: Moment | null) => {
    if (value) {
      setState((prevState) => ({
        ...prevState,
        examDate: value,
      }));
    }
  };

  const handleDateOfBirthChange = (value: Moment | null) => {
    if (value) {
      setState((prevState) => ({
        ...prevState,
        dateOfBirth: value,
      }));
    }
  };

  const handlePhoneNumberChange = (value: string, name: string) => {
    setState((prevState) => ({
      ...prevState,
      [name]: value || '',
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);

    const {
      firstName,
      lastName,
      gender,
      dateOfBirth,
      email,
      phoneNumber,
      selectedCurriculum,
      country,
      selectedGrade,
      examDate,
      schoolName,
      parentName,
      parentEmail,
      parentPhoneNumber,
      nationality,
      emergencyNumber,
      teachingStatus,
      availability,
      isCameraOn,
      trialNoticePeriod,
      classNoticePeriod,
      strengths,
      bio,
      bankCountry,
      bankName,
      bankCode,
      branchCode,
      bankBranch,
      accountNo,
      accountHolderName,
      billingEmail,
    } = state;

    const apiData: TUpdateUser = {
      billingEmail,
      firstname: firstName,
      lastname: lastName || '',
      email,
      phoneNumber,
      curriculum: selectedCurriculum?.value || '',
      grade: selectedGrade?.value || '',
    };

    if (gender) {
      apiData.gender = gender.value;
    }

    if (dateOfBirth) {
      apiData.dateOfBirth = dateOfBirth.clone().toISOString();
    }

    if (country) {
      apiData.country = country.value;
    }

    if (examDate) {
      apiData.examDate = examDate.clone().toISOString();
    }

    if (schoolName) {
      apiData.schoolName = schoolName;
    }

    if (parentName) {
      apiData.parentName = parentName;
    }

    if (parentEmail) {
      apiData.parentEmail = parentEmail;
    }

    if (parentPhoneNumber) {
      apiData.parentPhoneNumber = parentPhoneNumber;
    }

    if (nationality) {
      apiData.nationality = nationality;
    }

    if (emergencyNumber) {
      apiData.emergencyNumber = emergencyNumber;
    }

    if (
      teachingStatus ||
      availability ||
      isCameraOn ||
      trialNoticePeriod ||
      classNoticePeriod ||
      strengths.length ||
      bio
    ) {
      apiData.tutorDetails = {};

      if (teachingStatus) {
        apiData.tutorDetails.teachingStatus = teachingStatus.value;
      }
      if (availability) {
        apiData.tutorDetails.availability = availability.value;
      }
      if (isCameraOn) {
        apiData.tutorDetails.isCameraOn = isCameraOn.value;
      }
      if (trialNoticePeriod) {
        apiData.tutorDetails.trialNoticePeriod = trialNoticePeriod.value;
      }
      if (classNoticePeriod) {
        apiData.tutorDetails.classNoticePeriod = classNoticePeriod.value;
      }
      if (strengths.length) {
        apiData.tutorDetails.strengths = strengths;
      }
      if (bio) {
        apiData.tutorDetails.bio = bio;
      }
    }

    if (
      bankCountry ||
      bankName ||
      bankCode ||
      branchCode ||
      bankBranch ||
      accountNo ||
      accountHolderName
    ) {
      apiData.bankDetails = {};

      if (bankCountry) {
        apiData.bankDetails.bankCountry = bankCountry.value;
      }
      if (bankName) {
        apiData.bankDetails.bankName = bankName;
      }
      if (bankCode) {
        apiData.bankDetails.bankCode = bankCode;
      }
      if (branchCode) {
        apiData.bankDetails.branchCode = branchCode;
      }
      if (bankBranch) {
        apiData.bankDetails.bankBranch = bankBranch;
      }
      if (accountNo) {
        apiData.bankDetails.accountNo = accountNo;
      }
      if (accountHolderName) {
        apiData.bankDetails.accountHolderName = accountHolderName;
      }
    }

    if (Object.keys(apiData).length > 1) {
      await dispatch(updateUser(apiData));
    }

    setLoading(false);
  };

  const handleSelectChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectChange | null,
    name: string
  ) => {
    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleStrengthsChange = (
    event: React.SyntheticEvent<Element, Event>,
    values: TSelectChange[]
  ) => {
    setState((prevState) => ({
      ...prevState,
      strengths: values.map((p) => p.label),
    }));
  };

  return (
    <Box>
      <Stack
        flexDirection="row"
        justifyContent="space-between"
        marginBottom="24px"
      >
        <Typography variant="h6" fontSize="18px">
          Personal information
        </Typography>
        <LoadingButton
          {...primaryButtonProps}
          loading={isLoading}
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
        >
          Save
        </LoadingButton>
      </Stack>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={6}>
          <FormControl fullWidth>
            <TPTextField
              {...textFieldProps}
              name="firstName"
              size="small"
              label="First Name *"
              placeholder="First Name"
              value={state.firstName}
              onChange={handleInputChange}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <FormControl fullWidth>
            <TPTextField
              {...textFieldProps}
              name="lastName"
              size="small"
              label="Last Name *"
              placeholder="Last Name"
              value={state.lastName}
              onChange={handleInputChange}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <FormControl fullWidth>
            <Autocomplete
              size="small"
              value={state.gender}
              options={genderOptions}
              onChange={(event, value) =>
                handleSelectChange(event, value, 'gender')
              }
              popupIcon={TPAutoCompleteArrowIconComponent}
              renderInput={(params) => (
                <TPTextField
                  {...params}
                  {...textFieldProps}
                  size="small"
                  label="Gender *"
                  placeholder="Gender"
                />
              )}
            />
          </FormControl>
        </Grid>

        {dateOfBirthPermission && (
          <Grid item xs={12} sm={6} md={6}>
            <FormControl fullWidth>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <MobileDatePicker
                  disableCloseOnSelect={false}
                  value={state.dateOfBirth}
                  maxDate={moment()}
                  onChange={handleDateOfBirthChange}
                  label="Date of Birth *"
                  inputFormat="YYYY MMM DD"
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      placeholder="Date of Birth"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <InputAdornment position="end">
                            <DateRangeIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </FormControl>
          </Grid>
        )}

        <Grid item xs={12} sm={6} md={6}>
          <FormControl fullWidth>
            <TPTextField
              {...textFieldProps}
              name="email"
              size="small"
              label="Email *"
              placeholder="Email"
              value={state.email}
              onChange={handleInputChange}
            />
          </FormControl>
        </Grid>

        {billingEmailPermission ? (
          <Grid item xs={12} sm={6} md={6}>
            <FormControl fullWidth>
              <TPTextField
                {...textFieldProps}
                name="billingEmail"
                size="small"
                label="Billing Email"
                placeholder="Billing Email"
                value={state.billingEmail}
                onChange={handleInputChange}
              />
            </FormControl>
          </Grid>
        ) : null}

        <Grid item xs={12} sm={6} md={6}>
          <FormControl fullWidth>
            <InputLabel {...phoneInputLabelProps}>WhatsApp Number *</InputLabel>
            <PhoneInput
              {...phoneInputProps}
              defaultCountry={defaultCountryCode}
              placeholder="Enter phone number"
              value={state.phoneNumber}
              onChange={(value: string) =>
                handlePhoneNumberChange(value, 'phoneNumber')
              }
            />
          </FormControl>
        </Grid>

        {checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            profile: [USER_PERMISSION_INDIVIDUALS.PROFILE_CURRICULUM],
          },
        }) && (
          <>
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <Autocomplete
                  size="small"
                  value={state.selectedCurriculum}
                  options={curriculumOptions}
                  onChange={handleCurriculumChange}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  loading={curriculumState.status === 'loading'}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.value
                  }
                  getOptionKey={(option: TSelectOption) => option.value}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Curriculum"
                      placeholder="Curriculum"
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <Autocomplete
                  size="small"
                  value={state.selectedGrade}
                  options={gradeOptions}
                  onChange={handleGradeChange}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  loading={curriculumState.status === 'loading'}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.value
                  }
                  getOptionKey={(option: TSelectOption) => option.value}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Grade"
                      placeholder="Grade"
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </>
        )}

        <Grid item xs={12} sm={6} md={6}>
          <FormControl fullWidth>
            <Autocomplete
              size="small"
              value={state.country}
              options={countriesOptions}
              onChange={(event, value) =>
                handleSelectChange(event, value, 'country')
              }
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              popupIcon={TPAutoCompleteArrowIconComponent}
              renderInput={(params) => (
                <TPTextField
                  {...params}
                  {...textFieldProps}
                  size="small"
                  label="Time Zone *"
                  placeholder="Time Zone"
                />
              )}
            />
          </FormControl>
        </Grid>

        {nationalityPermission && (
          <Grid item xs={12} sm={6} md={6}>
            <FormControl fullWidth>
              <TPTextField
                {...textFieldProps}
                name="nationality"
                size="small"
                label="Nationality *"
                placeholder="Nationality"
                value={state.nationality}
                onChange={handleInputChange}
              />
            </FormControl>
          </Grid>
        )}

        {emergencyNumberPermission && (
          <Grid item xs={12} sm={6} md={6}>
            <FormControl fullWidth>
              <InputLabel {...phoneInputLabelProps}>
                Emergency WhatsApp Number *
              </InputLabel>
              <PhoneInput
                {...phoneInputProps}
                defaultCountry={defaultCountryCode}
                placeholder="Enter phone number"
                value={state.emergencyNumber}
                onChange={(value: string) =>
                  handlePhoneNumberChange(value, 'emergencyNumber')
                }
              />
            </FormControl>
          </Grid>
        )}
      </Grid>

      {checkUserPermission({
        authUser,
        tenant: appTenant,
        permissions: {
          profile: [USER_PERMISSION_INDIVIDUALS.PROFILE_EXAM_DATE],
        },
      }) && (
        <>
          <Divider
            flexItem
            sx={{ borderStyle: 'dashed', margin: '24px 0px' }}
          />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <MobileDatePicker
                    disableCloseOnSelect={false}
                    value={state.examDate}
                    onChange={handleExamDateChange}
                    label="Exam Date"
                    inputFormat="YYYY MMM DD"
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...textFieldProps}
                        size="small"
                        placeholder="Exam Date"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <InputAdornment position="end">
                              <DateRangeIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <TPTextField
                  {...textFieldProps}
                  name="schoolName"
                  size="small"
                  label="School Name"
                  placeholder="School Name"
                  value={state.schoolName}
                  onChange={handleInputChange}
                />
              </FormControl>
            </Grid>
          </Grid>
        </>
      )}

      {/* Parent information */}
      {checkUserPermission({
        authUser,
        tenant: appTenant,
        permissions: {
          profile: [USER_PERMISSION_INDIVIDUALS.PROFILE_PARENT],
        },
      }) && (
        <>
          <DashedDivider />
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            marginBottom="24px"
          >
            <Typography variant="h6" fontSize="18px">
              Parent information
            </Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <TPTextField
                  {...textFieldProps}
                  name="parentName"
                  size="small"
                  label="Full Name"
                  placeholder="Full Name"
                  value={state.parentName}
                  onChange={handleInputChange}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <TPTextField
                  {...textFieldProps}
                  name="parentEmail"
                  size="small"
                  label="Email"
                  placeholder="Email"
                  value={state.parentEmail}
                  onChange={handleInputChange}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <InputLabel {...phoneInputLabelProps}>
                  WhatsApp Number
                </InputLabel>
                <PhoneInput
                  {...phoneInputProps}
                  defaultCountry={defaultCountryCode}
                  placeholder="Enter phone number"
                  value={state.parentPhoneNumber}
                  onChange={(value: string) =>
                    handlePhoneNumberChange(value, 'parentPhoneNumber')
                  }
                />
              </FormControl>
            </Grid>
          </Grid>
        </>
      )}

      {/* Teaching Details */}
      {tutorDetailsPermission && (
        <>
          <Divider
            flexItem
            sx={{ borderStyle: 'dashed', margin: '24px 0px' }}
          />
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            marginBottom="24px"
          >
            <Typography variant="h6" fontSize="18px">
              Teaching Details
            </Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <Autocomplete
                  size="small"
                  value={state.teachingStatus}
                  options={teachingStatusOptions}
                  onChange={(event, value) =>
                    handleSelectChange(event, value, 'teachingStatus')
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.value
                  }
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Teaching Status *"
                      placeholder="Teaching Status"
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <Autocomplete
                  size="small"
                  value={state.availability}
                  options={availabilityOptions}
                  onChange={(event, value) =>
                    handleSelectChange(event, value, 'availability')
                  }
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Availability Status *"
                      placeholder="Availability Status"
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <Autocomplete
                  size="small"
                  value={state.isCameraOn}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  options={yesOrNoBoolOptions}
                  onChange={(event, value) =>
                    handleSelectChange(event, value, 'isCameraOn')
                  }
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Camera ON *"
                      placeholder="Camera ON"
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <Autocomplete
                  size="small"
                  value={state.trialNoticePeriod}
                  options={noticePeriodOptions}
                  onChange={(event, value) =>
                    handleSelectChange(event, value, 'trialNoticePeriod')
                  }
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Trial Notice Period"
                      placeholder="Trial Notice Period"
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <Autocomplete
                  size="small"
                  value={state.classNoticePeriod}
                  options={noticePeriodOptions}
                  onChange={(event, value) =>
                    handleSelectChange(event, value, 'classNoticePeriod')
                  }
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Class Notice Period"
                      placeholder="Class Notice Period"
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Autocomplete
                  multiple
                  disableClearable
                  size="small"
                  value={state.strengths.map((qt) => ({
                    label: qt,
                    value: qt,
                  }))}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  options={strengthOptions}
                  onChange={handleStrengthsChange}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      size="small"
                      label="Teacher's Strengths *"
                      placeholder="Teacher's Strengths"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        '& .MuiInputBase-root': {
                          minHeight: '52px',
                        },
                      }}
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <TPTextField
                  multiline
                  rows={5}
                  name="bio"
                  size="small"
                  label="Bio (This bio will be shared with customers to secure trials & tuition assignments for you.)"
                  placeholder="Bio"
                  value={state.bio}
                  onChange={handleInputChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
        </>
      )}

      {/* Bank Details */}
      {checkUserPermission({
        authUser,
        tenant: appTenant,
        permissions: {
          profile: [USER_PERMISSION_INDIVIDUALS.PROFILE_BANK],
        },
      }) && <BankDetails {...{ state, setState }} />}
    </Box>
  );
};

export default BasicDetails;
